import React, { useEffect, useRef, useState } from "react";
import api, { formatIDR, formatDate, API, exportExcel } from "@/lib/api";
import ImportProgressDialog from "@/components/ImportProgressDialog";
import { DateRangeFilter, firstOfMonth, todayStr, buildDateQuery } from "@/components/DateRangeFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, PencilSimple, Lock, Download, Upload, MagnifyingGlass, ClockCounterClockwise, Printer, Hash, FileXls, Robot } from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePaginated } from "@/components/Paginator";

export default function Accounting() {
  const [tab, setTab] = useState("dashboard");
  const [accounts, setAccounts] = useState([]);
  const [journal, setJournal] = useState([]);
  const [pnl, setPnl] = useState(null);
  const [balance, setBalance] = useState(null);

  const [journalOpen, setJournalOpen] = useState(false);
  const [journalEdit, setJournalEdit] = useState(null);
  const [journalDel, setJournalDel] = useState(null);
  const [journalHist, setJournalHist] = useState(null);
  const [search, setSearch] = useState("");
  const [reconciling, setReconciling] = useState(false);

  const reconcileBalance = async () => {
    setReconciling(true);
    try {
      const r = await api.post("/accounting/reports/balance-sheet/reconcile");
      if (r.data.reconciled) toast.success(r.data.message, { duration: 6000 });
      else toast.success(r.data.message || "Neraca sudah seimbang, tidak perlu jurnal");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyamakan neraca"); }
    finally { setReconciling(false); }
  };

  const today = () => new Date().toISOString().slice(0, 10);

  const [journalForm, setJournalForm] = useState({ date: today(), reference: "", description: "", lines: [{ account_code: "", account_name: "", debit: 0, credit: 0 }, { account_code: "", account_name: "", debit: 0, credit: 0 }] });

  const load = () => {
    api.get("/accounting/accounts").then((r) => setAccounts(r.data));
    api.get(`/accounting/journal${buildDateQuery(dateRange)}`).then((r) => setJournal(r.data));
    api.get("/accounting/reports/pnl").then((r) => setPnl(r.data));
    api.get("/accounting/reports/balance-sheet").then((r) => setBalance(r.data));
  };
  const [dateRange, setDateRange] = useState({ from: firstOfMonth(), to: todayStr() });
  useEffect(() => { load(); }, [dateRange]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterQ = (arr, fields) => {
    const q = search.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter(r => fields.some(f => (r[f] || "").toString().toLowerCase().includes(q)));
  };
  const filteredJournal = filterQ(journal, ["number", "reference", "description", "source_type"]);
  const filteredAccounts = filterQ(accounts, ["code", "name", "subtype", "type"]);
  const pgJournal = usePaginated(filteredJournal);

  const [accDlg, setAccDlg] = useState(false);
  const [accEdit, setAccEdit] = useState(null);
  const [accForm, setAccForm] = useState({ code: "", name: "", type: "asset", subtype: "", parent_id: "", is_parent: false });
  const [subtypes, setSubtypes] = useState({});
  const ACC_TYPES = [["asset", "Aset"], ["liability", "Liabilitas"], ["equity", "Ekuitas"], ["revenue", "Pendapatan"], ["expense", "Beban"]];

  useEffect(() => {
    api.get("/accounting/account-subtypes").then((r) => setSubtypes(r.data)).catch(() => {});
  }, []);

  const [renameAcc, setRenameAcc] = useState(null);
  const [renameCode, setRenameCode] = useState("");
  const openRenameCode = (a) => { setRenameAcc(a); setRenameCode(a.code); };
  const doRenameCode = async () => {
    if (!renameAcc) return;
    const nc = (renameCode || "").trim();
    if (!nc || nc === renameAcc.code) { setRenameAcc(null); return; }
    try {
      await api.post(`/accounting/accounts/${renameAcc.id}/rename-code`, { new_code: nc });
      toast.success(`Nomor akun ${renameAcc.code} → ${nc} (semua referensi diperbarui)`);
      setRenameAcc(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengganti nomor"); }
  };

  const openAccNew = () => { setAccEdit(null); setAccForm({ code: "", name: "", type: "asset", subtype: "", parent_id: "", is_parent: false }); setAccDlg(true); };
  const openAccEdit = (a) => { setAccEdit(a); setAccForm({ code: a.code, name: a.name, type: a.type, subtype: a.subtype || "", parent_id: a.parent_id || "", is_parent: !!a.is_parent }); setAccDlg(true); };
  const saveAcc = async () => {
    try {
      const payload = { ...accForm, parent_id: accForm.parent_id || null };
      if (accEdit) await api.put(`/accounting/accounts/${accEdit.id}`, payload);
      else await api.post("/accounting/accounts", payload);
      toast.success(accEdit ? "Akun diperbarui" : "Akun baru tersimpan");
      setAccDlg(false); setAccEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan akun"); }
  };
  const delAcc = async (a) => {
    if (!window.confirm(`Hapus akun ${a.code} - ${a.name}?`)) return;
    try { await api.delete(`/accounting/accounts/${a.id}`); toast.success("Akun dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus akun"); }
  };
  const accFileRef = useRef(null);
  const journalFileRef = useRef(null);
  const [importProg, setImportProg] = useState(null); // { loading, fileName, result }
  const downloadJournalTemplate = async () => {
    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/accounting/journal/import-template`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Gagal unduh template");
      const blob = await resp.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "template_import_jurnal.xlsx"; a.click();
    } catch (e) { toast.error(e.message || "Gagal unduh template"); }
  };
  const importJournal = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setImportProg({ loading: true, fileName: f.name });
    try {
      const r = await api.post("/accounting/journal/import", fd, { onUploadProgress: (ev) => {
        if (ev.total) setImportProg(p => ({ ...(p || {}), loading: true, fileName: f.name, uploadPct: Math.round((ev.loaded / ev.total) * 100) }));
      }});
      setImportProg({ loading: false, fileName: f.name, result: r.data });
      toast.success(`Impor selesai: ${r.data.created} jurnal dibuat` + (r.data.errors?.length ? `, ${r.data.errors.length} error` : ""));
      load();
    } catch (err) { setImportProg(null); toast.error(err?.response?.data?.detail || "Gagal impor jurnal"); }
    finally { e.target.value = ""; }
  };
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeText, setPurgeText] = useState("");
  const [purging, setPurging] = useState(false);
  const purgeAllJournals = async () => {
    if (purgeText.trim().toUpperCase() !== "HAPUS") return;
    setPurging(true);
    try {
      const r = await api.post("/accounting/journal/purge-all");
      if (r.data.backup) {
        const blob = new Blob([JSON.stringify(r.data.backup, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `backup_jurnal_${new Date().toISOString().slice(0, 10)}.json`; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
      toast.success(`Backup JSON terunduh. Dibersihkan ${r.data.deleted_journals} jurnal; aset direset`, { duration: 8000 });
      setPurgeOpen(false); setPurgeText(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus jurnal"); }
    finally { setPurging(false); }
  };
  const exportJournal = async () => {
    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/accounting/journal/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Gagal export");
      const blob = await resp.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `jurnal_umum_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.message || "Gagal export"); }
  };
  // ==== Impor dari Google Sheets (AI) ====
  const [sheetsOpen, setSheetsOpen] = useState(false);
  const [sheetsUrl, setSheetsUrl] = useState("");
  const [sheetsInstruction, setSheetsInstruction] = useState("");
  const [sheetsScanning, setSheetsScanning] = useState(false);
  const [sheetsPreview, setSheetsPreview] = useState(null); // { source, summary, count, actions }
  const [sheetsExecuting, setSheetsExecuting] = useState(false);
  const [sheetsResult, setSheetsResult] = useState(null); // { executed, errors }
  const SHEET_ACTION_LABELS = { sales_invoice: "Faktur Penjualan", sales_order: "Sales Order", sales_quotation: "Penawaran", purchase_invoice: "Faktur Pembelian", purchase_order: "Purchase Order", expense: "Beban", cash_receipt: "Uang Masuk", cash_payment: "Uang Keluar" };
  const sheetActionAmount = (a) => {
    const d = a?.data || {};
    if (Number(d.amount)) return Number(d.amount);
    if (Number(d.total)) return Number(d.total);
    const items = Array.isArray(d.items) ? d.items : [];
    const s = items.reduce((t, it) => t + (Number(it.quantity ?? it.qty) || 0) * (Number(it.price ?? it.unit_price) || 0), 0);
    return s || null;
  };
  const scanSheets = async () => {
    if (!sheetsUrl.trim()) { toast.error("Tempel URL Google Sheets dulu"); return; }
    setSheetsScanning(true); setSheetsPreview(null); setSheetsResult(null);
    try {
      const fd = new FormData();
      fd.append("sheets_url", sheetsUrl.trim());
      fd.append("instruction", sheetsInstruction.trim() || "Buat penjurnalan sesuai isi tabel. Pakai master data (partner/produk/akun) yang tersedia.");
      fd.append("execute", "false");
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/ai/analyze-file`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const r = await resp.json();
      if (!resp.ok || !r.parsed) throw new Error(r.detail || r.error || "AI tidak bisa membaca sheet");
      if (!r.count) { toast.error("AI tidak menemukan transaksi di sheet ini"); return; }
      setSheetsPreview(r);
    } catch (e) { toast.error(e.message || "Gagal membaca Google Sheets"); }
    finally { setSheetsScanning(false); }
  };
  const executeSheetsActions = async () => {
    if (!sheetsPreview?.actions?.length) return;
    setSheetsExecuting(true);
    try {
      const r = await api.post("/ai/analyze-file-execute", { actions: sheetsPreview.actions });
      setSheetsResult(r.data);
      toast.success(`${r.data.executed?.length || 0} aksi tersimpan${r.data.errors?.length ? `, ${r.data.errors.length} gagal` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal eksekusi aksi"); }
    finally { setSheetsExecuting(false); }
  };
  const downloadCoaTemplate = async () => {
    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/accounting/accounts/import-template`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Gagal unduh template");
      const blob = await resp.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "template_import_coa.xlsx"; a.click();
    } catch (e) { toast.error(e.message || "Gagal unduh template"); }
  };
  const importCoa = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setImportProg({ loading: true, fileName: f.name });
    try {
      const r = await api.post("/accounting/accounts/import", fd);
      setImportProg({ loading: false, fileName: f.name, result: r.data });
      load();
    } catch (err) { setImportProg(null); toast.error(err?.response?.data?.detail || "Gagal impor COA"); }
    finally { e.target.value = ""; }
  };
  // Kandidat akun induk saat form: harus is_parent, tipe sama, dan bukan akun yang sedang diedit
  const parentCandidates = accounts.filter(a => a.is_parent && a.type === accForm.type && a.id !== accEdit?.id);
  // Susun ulang tampilan tabel: parent → children (rekursif, tanpa batas level)
  const orderedAccounts = (() => {
    const byId = new Map(filteredAccounts.map(a => [a.id, a]));
    const result = [];
    const visited = new Set();
    const emit = (a, depth) => {
      if (visited.has(a.id)) return; // anti-siklus
      visited.add(a.id);
      result.push({ ...a, _depth: depth });
      filteredAccounts.filter(c => c.parent_id === a.id).forEach(c => emit(c, depth + 1));
    };
    // Root = tanpa parent ATAU parent-nya sudah tidak ada (orphan tetap tampil)
    filteredAccounts.filter(a => !a.parent_id || !byId.get(a.parent_id)).forEach(r => emit(r, 0));
    return result;
  })();

  const emptyJournalForm = () => ({ date: today(), reference: "", description: "", lines: [{ account_code: "", account_name: "", debit: 0, credit: 0 }, { account_code: "", account_name: "", debit: 0, credit: 0 }] });

  const submitJournal = async () => {
    try {
      const lines = journalForm.lines.map(l => ({ ...l, debit: Number(l.debit || 0), credit: Number(l.credit || 0) }));
      if (journalEdit) await api.put(`/accounting/journal/${journalEdit.id}`, { ...journalForm, lines });
      else await api.post("/accounting/journal", { ...journalForm, lines });
      toast.success(journalEdit ? "Jurnal diperbarui" : "Jurnal tersimpan");
      setJournalOpen(false);
      setJournalEdit(null);
      setJournalForm(emptyJournalForm());
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal simpan jurnal"); }
  };

  const openEditJournal = (j) => {
    setJournalEdit(j);
    setJournalForm({
      date: (j.date || "").slice(0, 10), reference: j.reference || "", description: j.description || "",
      lines: (j.lines || []).map(l => ({ account_code: l.account_code || "", account_name: l.account_name || "", debit: l.debit || 0, credit: l.credit || 0 })),
    });
    setJournalOpen(true);
  };

  const deleteJournal = async () => {
    if (!journalDel) return;
    try {
      await api.delete(`/accounting/journal/${journalDel.id}`);
      toast.success("Jurnal berhasil dihapus");
      setJournalDel(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus jurnal"); }
  };

  const [printOpen, setPrintOpen] = useState(false);
  const [printRange, setPrintRange] = useState({ date_from: firstOfMonth(), date_to: today() });
  const [printAllTime, setPrintAllTime] = useState(true);

  const printFinancialStatement = async () => {
    try {
      const token = localStorage.getItem("nusa_token");
      const qs = printAllTime ? "?all_time=true" : `?date_from=${printRange.date_from}&date_to=${printRange.date_to}`;
      const resp = await fetch(`${API}/accounting/reports/financial-statement/pdf${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err.detail || "Gagal mencetak"); }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setPrintOpen(false);
    } catch (e) { toast.error(e.message || "Gagal mencetak"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl" data-testid="accounting-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="font-serif-display text-4xl font-medium text-slate-900">Akuntansi</h1>
          <p className="text-sm text-slate-500 mt-1">Jurnal umum, chart of accounts, dan laporan keuangan. Invoice dibuat di menu Penjualan, beban dicatat di menu Kas &amp; Bank.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="dashboard" data-testid="tab-acc-dashboard">Ringkasan</TabsTrigger>
            <TabsTrigger value="journal" data-testid="tab-journal">Jurnal Umum</TabsTrigger>
            <TabsTrigger value="accounts" data-testid="tab-accounts">Chart of Accounts</TabsTrigger>
          </TabsList>
          <div className="relative w-72">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari jurnal / akun…" className="pl-8 h-9" data-testid="accounting-search-input" />
          </div>
        </div>

        <TabsContent value="dashboard" className="mt-6 grid lg:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="pnl-report">
            <h3 className="font-serif-display text-2xl font-medium">Laba Rugi</h3>
            {pnl && (
              <div className="mt-4 space-y-3 text-sm">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Pendapatan</div>
                {(pnl.revenue_groups || []).map((g) => (
                  <div key={g.name}>
                    <div className="flex justify-between font-medium text-slate-700"><span>{g.name}</span><span className="tabular-nums">{formatIDR(g.total)}</span></div>
                    {g.accounts.map((a) => (
                      <div key={a.code || a.name} className="flex justify-between pl-4 text-xs py-0.5"><span className="text-slate-500">{a.code} {a.name}</span><span className="tabular-nums text-emerald-700">{formatIDR(a.amount)}</span></div>
                    ))}
                  </div>
                ))}
                <div className="flex justify-between font-semibold border-t border-slate-200 pt-1.5"><span>Total Pendapatan</span><span className="tabular-nums text-emerald-700">{formatIDR(pnl.revenue)}</span></div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold pt-2">Beban</div>
                {(pnl.expense_groups || []).map((g) => (
                  <div key={g.name}>
                    <div className="flex justify-between font-medium text-slate-700"><span>{g.name}</span><span className="tabular-nums">{formatIDR(g.total)}</span></div>
                    {g.accounts.map((a) => (
                      <div key={a.code || a.name} className="flex justify-between pl-4 text-xs py-0.5"><span className="text-slate-500">{a.code} {a.name}</span><span className="tabular-nums text-rose-700">{formatIDR(a.amount)}</span></div>
                    ))}
                  </div>
                ))}
                <div className="flex justify-between font-semibold border-t border-slate-200 pt-1.5"><span>Total Beban</span><span className="tabular-nums text-rose-700">({formatIDR(pnl.expenses)})</span></div>
                <div className="border-t-2 border-slate-300 pt-2 flex justify-between font-bold"><span>Laba Bersih</span><span className="tabular-nums text-slate-900">{formatIDR(pnl.net_income)}</span></div>
              </div>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="balance-report">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-serif-display text-2xl font-medium">Neraca</h3>
              <Button size="sm" variant="outline" onClick={() => setPrintOpen(true)} data-testid="print-financial-btn"><Printer size={14} className="mr-1" />Cetak Laporan Keuangan</Button>
            </div>
            {balance && (
              <div className="mt-4 space-y-3 text-sm">
                {(balance.positions || []).length === 0 && <div className="text-slate-500">Belum ada saldo.</div>}
                {[["Aset", balance.asset_groups, balance.total_assets], ["Liabilitas", balance.liability_groups, balance.total_liabilities], ["Ekuitas", balance.equity_groups, balance.total_equity]].map(([label, groups, total]) => (
                  <div key={label}>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
                    {(groups || []).map((g) => (
                      <div key={g.name} className="mt-1">
                        <div className="flex justify-between font-medium text-slate-700"><span>{g.name}</span><span className="tabular-nums">{formatIDR(g.total)}</span></div>
                        {g.accounts.map((a) => (
                          <div key={a.code || a.name} className="flex justify-between pl-4 text-xs py-0.5"><span className="text-slate-500">{a.code} {a.name}</span><span className="tabular-nums">{formatIDR(a.amount)}</span></div>
                        ))}
                      </div>
                    ))}
                    <div className="flex justify-between font-semibold border-t border-slate-200 pt-1 mt-1"><span>Total {label}</span><span className="tabular-nums">{formatIDR(total || 0)}</span></div>
                  </div>
                ))}
                <div className="mt-2 flex items-center justify-between gap-3">
                  <div className={`text-xs font-semibold ${balance.balanced ? "text-emerald-700" : "text-rose-700"}`} data-testid="balance-check">
                    {balance.balanced
                      ? "✓ Seimbang: Aset = Liabilitas + Ekuitas"
                      : `⚠ Tidak seimbang — selisih ${formatIDR(Math.abs((balance.total_assets || 0) - (balance.total_liabilities || 0) - (balance.total_equity || 0)))}. Klik "Samakan Neraca" untuk membuat jurnal penyeimbang ke akun Saldo Awal.`}
                  </div>
                  <Button size="sm" variant="outline" onClick={reconcileBalance} disabled={reconciling} data-testid="reconcile-balance-btn">
                    {reconciling ? "Memproses…" : "Samakan Neraca"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="journal" className="mt-6">
          <div className="flex justify-end gap-2 mb-3">
            <input ref={journalFileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={importJournal} data-testid="journal-import-input" />
            <Button variant="outline" onClick={downloadJournalTemplate} data-testid="journal-template-btn"><Download size={14} className="mr-1" />Template</Button>
            <Button variant="outline" onClick={exportJournal} className="border-slate-300 text-slate-800 hover:bg-slate-100" data-testid="journal-export-btn"><Download size={14} className="mr-1" />Export Excel</Button>
            <Button variant="outline" onClick={() => journalFileRef.current?.click()} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="journal-import-btn" title="Impor jurnal massal dari Excel — untuk migrasi dari sistem lama"><Upload size={14} className="mr-1" />Impor Jurnal</Button>
            <Button variant="outline" onClick={() => { setSheetsPreview(null); setSheetsResult(null); setSheetsOpen(true); }} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="sheets-import-btn" title="AI membaca Google Sheets publik dan menyusun aksi penjurnalan untuk direview"><FileXls size={14} className="mr-1" />Impor dari Google Sheets</Button>
            <Button variant="outline" onClick={() => { setPurgeText(""); setPurgeOpen(true); }} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="journal-purge-btn" title="Hapus semua jurnal (impor + penyusutan sistem) & reset aset ke status belum disusutkan">🧨 Hapus Semua Jurnal</Button>
            <Dialog open={purgeOpen} onOpenChange={(o) => { if (!purging) { setPurgeOpen(o); if (!o) setPurgeText(""); } }}>
              <DialogContent className="max-w-md" data-testid="journal-purge-dialog">
                <DialogHeader><DialogTitle className="text-rose-700">Hapus Semua Jurnal?</DialogTitle></DialogHeader>
                <div className="space-y-4 text-sm text-slate-600">
                  <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-rose-800" data-testid="journal-purge-warning">
                    Tindakan ini <b>SANGAT DESTRUKTIF</b>: seluruh jurnal (manual, impor, & penyusutan sistem) akan dihapus permanen dan semua aset tetap direset ke status belum disusutkan. <b>Tidak dapat dibatalkan.</b>
                  </div>
                  <p>Snapshot seluruh data jurnal akan <b>otomatis terunduh sebagai file JSON</b> ke komputer Anda sebelum penghapusan dieksekusi.</p>
                  <div>
                    <Label>Ketik <b>HAPUS</b> untuk melanjutkan</Label>
                    <Input value={purgeText} onChange={(e) => setPurgeText(e.target.value)} placeholder="HAPUS" className="mt-1 uppercase" disabled={purging} data-testid="journal-purge-confirm-input" autoFocus />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => { setPurgeOpen(false); setPurgeText(""); }} disabled={purging} data-testid="journal-purge-cancel-btn">Batal</Button>
                    <Button onClick={purgeAllJournals} disabled={purging || purgeText.trim().toUpperCase() !== "HAPUS"} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="journal-purge-confirm-btn">
                      {purging ? "Membackup & Menghapus…" : "Backup & Hapus Semua"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={sheetsOpen} onOpenChange={(o) => { if (!sheetsScanning && !sheetsExecuting) { setSheetsOpen(o); if (!o) { setSheetsPreview(null); setSheetsResult(null); } } }}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0" data-testid="sheets-import-dialog">
                <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle className="flex items-center gap-2"><FileXls size={18} className="text-amber-600" />Impor dari Google Sheets</DialogTitle></DialogHeader>
                <div className="space-y-4 min-h-0 overflow-y-auto px-6 pt-4 pb-6 text-sm">
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 text-xs" data-testid="sheets-import-hint">
                    Pastikan sheet dibagikan publik: <b>Share → Anyone with the link (Viewer)</b>. Tempel URL lengkap — tab tertentu didukung via parameter <code>gid</code> pada URL. AI akan membaca isi tabel dan menyusun aksi penjurnalan untuk Anda review sebelum dieksekusi.
                  </div>
                  <div>
                    <Label>URL Google Sheets</Label>
                    <Input value={sheetsUrl} onChange={(e) => setSheetsUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…/edit?usp=sharing" className="mt-1" disabled={sheetsScanning || sheetsExecuting} data-testid="sheets-url-input" />
                  </div>
                  <div>
                    <Label>Instruksi tambahan untuk AI <span className="text-slate-400 font-normal">(opsional)</span></Label>
                    <Textarea rows={2} value={sheetsInstruction} onChange={(e) => setSheetsInstruction(e.target.value)} placeholder='Contoh: "Semua baris adalah beban operasional" atau "Kolom D adalah tanggal transaksi"' className="mt-1" disabled={sheetsScanning || sheetsExecuting} data-testid="sheets-instruction-input" />
                  </div>
                  {!sheetsPreview && (
                    <div className="flex justify-end">
                      <Button onClick={scanSheets} disabled={sheetsScanning || !sheetsUrl.trim()} className="bg-slate-900" data-testid="sheets-scan-btn">
                        {sheetsScanning ? "AI sedang menganalisis…" : <><Robot size={15} className="mr-1" />Scan & Analisis AI</>}
                      </Button>
                    </div>
                  )}
                  {sheetsPreview && (
                    <div className="space-y-3" data-testid="sheets-preview">
                      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-800 text-xs" data-testid="sheets-summary">
                        <b>{sheetsPreview.count} aksi</b> ditemukan dari <i>{sheetsPreview.source}</i>.{sheetsPreview.summary ? ` ${sheetsPreview.summary}` : ""}
                      </div>
                      <div className="rounded-md border border-slate-200 overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-8">#</TableHead>
                              <TableHead className="w-36">Aksi</TableHead>
                              <TableHead className="w-28">Tanggal</TableHead>
                              <TableHead>Keterangan</TableHead>
                              <TableHead className="text-right w-32">Nominal</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sheetsPreview.actions.map((a, i) => {
                              const amt = sheetActionAmount(a);
                              return (
                                <TableRow key={i} data-testid={`sheets-action-row-${i}`}>
                                  <TableCell className="text-slate-400">{i + 1}</TableCell>
                                  <TableCell className="font-medium">{SHEET_ACTION_LABELS[a.action] || a.action}</TableCell>
                                  <TableCell className="text-slate-500">{a.data?.date || "-"}</TableCell>
                                  <TableCell className="text-slate-600">{a.confirmation_message || a.data?.description || "-"}</TableCell>
                                  <TableCell className="text-right tabular-nums">{amt != null ? formatIDR(amt) : "-"}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                      {sheetsResult && (
                        <div className="space-y-2" data-testid="sheets-result">
                          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-800 text-xs">
                            ✓ {sheetsResult.executed?.length || 0} aksi berhasil tersimpan.
                          </div>
                          {(sheetsResult.errors || []).length > 0 && (
                            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-rose-800 text-xs space-y-1" data-testid="sheets-errors">
                              {sheetsResult.errors.map((er, i) => <div key={i}>✗ {SHEET_ACTION_LABELS[er.action] || er.action || "aksi"}: {er.error}</div>)}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex justify-between gap-2">
                        <Button variant="outline" onClick={() => { setSheetsPreview(null); setSheetsResult(null); }} disabled={sheetsScanning || sheetsExecuting} data-testid="sheets-rescan-btn">Scan Ulang</Button>
                        {!sheetsResult && (
                          <Button onClick={executeSheetsActions} disabled={sheetsExecuting || !sheetsPreview.actions.length} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sheets-execute-btn">
                            {sheetsExecuting ? "Mengeksekusi…" : `✓ Eksekusi Semua (${sheetsPreview.count} aksi)`}
                          </Button>
                        )}
                        {sheetsResult && (
                          <Button onClick={() => setSheetsOpen(false)} className="bg-slate-900" data-testid="sheets-done-btn">Selesai</Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={journalOpen} onOpenChange={(o) => { setJournalOpen(o); if (!o) setJournalEdit(null); }}>
              <DialogTrigger asChild><Button className="bg-slate-900" data-testid="new-journal-btn" onClick={() => { setJournalEdit(null); setJournalForm(emptyJournalForm()); }}><Plus size={16} className="mr-1" /> Jurnal</Button></DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
                <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>{journalEdit ? "Edit Jurnal Umum" : "Jurnal Umum"}</DialogTitle></DialogHeader>
                <div className="space-y-3 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Tanggal</Label><Input type="date" value={journalForm.date} onChange={(e) => setJournalForm({ ...journalForm, date: e.target.value })} /></div>
                    <div><Label>Referensi</Label><Input value={journalForm.reference} onChange={(e) => setJournalForm({ ...journalForm, reference: e.target.value })} /></div>
                  </div>
                  <div>
                    <Label>Deskripsi</Label>
                    <Textarea rows={3} placeholder="Tulis deskripsi lengkap transaksi ini, boleh kalimat panjang..."
                      value={journalForm.description}
                      onChange={(e) => setJournalForm({ ...journalForm, description: e.target.value })}
                      data-testid="journal-desc-input" />
                  </div>
                  <div>
                    <Label>Baris Jurnal (Debit = Kredit)</Label>
                    <div className="grid grid-cols-12 gap-2 mt-2" data-testid="journal-lines-header">
                      <span className="col-span-6 text-[11px] uppercase tracking-wider text-slate-500">Akun</span>
                      <span className="col-span-3 text-[11px] uppercase tracking-wider text-slate-500">Debit (Rp)</span>
                      <span className="col-span-3 text-[11px] uppercase tracking-wider text-slate-500">Kredit (Rp)</span>
                    </div>
                    {journalForm.lines.map((l, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 mt-2">
                        <Select value={l.account_code} onValueChange={(v) => {
                          const acc = accounts.find(a => a.code === v);
                          const lines = [...journalForm.lines];
                          lines[idx].account_code = v; lines[idx].account_name = acc?.name || "";
                          setJournalForm({ ...journalForm, lines });
                        }}>
                          <SelectTrigger className="col-span-6" data-testid={`journal-acc-${idx}`}><SelectValue placeholder="Pilih akun" /></SelectTrigger>
                          <SelectContent>{accounts.map(a => <SelectItem key={a.code} value={a.code}>{a.code} - {a.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <MoneyInput className="col-span-3" placeholder="Debit" value={l.debit} onChange={(e) => {
                          const lines = [...journalForm.lines]; lines[idx].debit = e.target.value; setJournalForm({ ...journalForm, lines });
                        }} data-testid={`journal-debit-${idx}`} />
                        <MoneyInput className="col-span-3" placeholder="Kredit" value={l.credit} onChange={(e) => {
                          const lines = [...journalForm.lines]; lines[idx].credit = e.target.value; setJournalForm({ ...journalForm, lines });
                        }} data-testid={`journal-credit-${idx}`} />
                      </div>
                    ))}
                    <Button variant="outline" size="sm" className="mt-2" onClick={() => setJournalForm({ ...journalForm, lines: [...journalForm.lines, { account_code: "", account_name: "", debit: 0, credit: 0 }] })} data-testid="journal-add-line-btn">+ Baris</Button>
                  </div>
                  <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 border-t border-slate-200 bg-background px-6 py-3">
                    <Button className="w-full bg-slate-900" onClick={submitJournal} data-testid="journal-save-btn">{journalEdit ? "Simpan Perubahan" : "Simpan Jurnal"}</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={!!journalDel} onOpenChange={(o) => { if (!o) setJournalDel(null); }}>
              <DialogContent className="max-w-md" data-testid="journal-delete-dialog">
                <DialogHeader><DialogTitle>Hapus Jurnal Umum?</DialogTitle></DialogHeader>
                <div className="text-sm text-slate-600 space-y-2">
                  <p>Jurnal berikut akan <b>dihapus permanen</b> dan tidak bisa dikembalikan:</p>
                  <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs space-y-1" data-testid="journal-delete-summary">
                    <div><b>Tanggal:</b> {journalDel ? formatDate(journalDel.date) : ""}</div>
                    <div><b>Referensi:</b> {journalDel?.reference || "-"}</div>
                    <div><b>Deskripsi:</b> {journalDel?.description}</div>
                    <div><b>Total:</b> {journalDel ? formatIDR(journalDel.total) : ""}</div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-2">
                  <Button variant="outline" onClick={() => setJournalDel(null)} data-testid="journal-del-cancel-btn">Batal</Button>
                  <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={deleteJournal} data-testid="journal-del-confirm-btn">Hapus Permanen</Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={!!journalHist} onOpenChange={(o) => { if (!o) setJournalHist(null); }}>
              <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="journal-history-dialog">
                <DialogHeader><DialogTitle>Riwayat Revisi Jurnal — {journalHist?.reference || journalHist?.description?.slice(0, 40)}</DialogTitle></DialogHeader>
                {journalHist && (
                  <div className="space-y-4 text-sm">
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs">
                      <div className="font-semibold text-emerald-800 mb-1">Saat ini (versi terbaru)</div>
                      <div><b>Tanggal:</b> {formatDate(journalHist.date)} · <b>Ref:</b> {journalHist.reference || "-"} · <b>Total:</b> {formatIDR(journalHist.total)}</div>
                      <div><b>Deskripsi:</b> {journalHist.description}</div>
                      <div className="overflow-x-auto">
                      <table className="w-full mt-1.5 text-[11px] min-w-[280px]">
                        <thead><tr className="text-slate-500"><th className="text-left">Akun</th><th className="text-right">DR</th><th className="text-right">CR</th></tr></thead>
                        <tbody>{(journalHist.lines || []).map((l, i) => (
                          <tr key={i}><td>{l.account_code} {l.account_name}</td><td className="text-right tabular-nums">{formatIDR(l.debit || 0)}</td><td className="text-right tabular-nums">{formatIDR(l.credit || 0)}</td></tr>
                        ))}</tbody>
                      </table>
                      </div>
                    </div>
                    {(journalHist.audit_log || []).length === 0 ? (
                      <p className="text-slate-500 text-xs italic">Belum ada revisi. Jurnal ini belum pernah diubah setelah dibuat.</p>
                    ) : (
                      <div className="space-y-3">
                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Riwayat Revisi ({(journalHist.audit_log || []).length})</div>
                        {[...(journalHist.audit_log || [])].reverse().map((h, idx) => (
                          <div key={idx} className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs" data-testid={`journal-hist-item-${idx}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold text-slate-700">Versi sebelum revisi #{(journalHist.audit_log || []).length - idx}</span>
                              <span className="text-slate-500">{h.revised_at ? formatDate(h.revised_at.slice(0, 10)) : ""} · {h.revised_by || "-"}</span>
                            </div>
                            <div><b>Tanggal:</b> {formatDate((h.before?.date || ""))} · <b>Ref:</b> {h.before?.reference || "-"} · <b>Total:</b> {formatIDR(h.before?.total || 0)}</div>
                            <div><b>Deskripsi:</b> {h.before?.description || "-"}</div>
                            <div className="overflow-x-auto">
                            <table className="w-full mt-1.5 text-[11px] min-w-[280px]">
                              <thead><tr className="text-slate-500"><th className="text-left">Akun</th><th className="text-right">DR</th><th className="text-right">CR</th></tr></thead>
                              <tbody>{(h.before?.lines || []).map((l, i) => (
                                <tr key={i}><td>{l.account_code} {l.account_name}</td><td className="text-right tabular-nums">{formatIDR(l.debit || 0)}</td><td className="text-right tabular-nums">{formatIDR(l.credit || 0)}</td></tr>
                              ))}</tbody>
                            </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-end mt-3">
                  <Button variant="outline" onClick={() => setJournalHist(null)}>Tutup</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="mb-2 flex justify-end"><DateRangeFilter range={dateRange} onChange={setDateRange} testidPrefix="journal" /></div>
          <div className="bg-white border border-slate-200 rounded-md" data-testid="journal-table">
            <Table>
              <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Ref</TableHead><TableHead>Deskripsi</TableHead><TableHead>Akun Debit</TableHead><TableHead>Akun Kredit</TableHead><TableHead>Total</TableHead><TableHead className="w-20"></TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredJournal.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500 text-sm">{search ? "Tidak ada hasil yang cocok." : "Belum ada jurnal."}</TableCell></TableRow> : pgJournal.paged.map((j) => (
                  <TableRow key={j.id} data-testid={`journal-row-${j.id}`}>
                    <TableCell>{formatDate(j.date)}</TableCell>
                    <TableCell className="font-mono-plex text-xs">{j.reference || "-"}</TableCell>
                    <TableCell>{j.description}{j.source ? <span className={`ml-1 text-[10px] px-1 rounded ${["ai_auto_entry","ai_receipt_scan"].includes(j.source) ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`} title={["ai_auto_entry","ai_receipt_scan"].includes(j.source) ? "Dijurnal oleh AI — bisa diedit/dihapus manual" : `Dibuat otomatis oleh modul: ${j.source}`}>{["ai_auto_entry","ai_receipt_scan"].includes(j.source) ? "AI" : "Otomatis"}</span> : null}</TableCell>
                    <TableCell className="align-top" data-testid={`journal-debit-accts-${j.id}`}>
                      <div className="space-y-0.5 min-w-[160px]">
                        {(j.lines || []).filter(l => Number(l.debit) > 0).map((l, i) => (
                          <div key={i} className="text-xs leading-tight">
                            <span className="font-mono-plex text-slate-500">{l.account_code}</span> {l.account_name}
                            <span className="ml-1 tabular-nums text-emerald-700">{formatIDR(l.debit)}</span>
                          </div>
                        ))}
                        {!(j.lines || []).some(l => Number(l.debit) > 0) && <span className="text-xs text-slate-400">-</span>}
                      </div>
                    </TableCell>
                    <TableCell className="align-top" data-testid={`journal-credit-accts-${j.id}`}>
                      <div className="space-y-0.5 min-w-[160px]">
                        {(j.lines || []).filter(l => Number(l.credit) > 0).map((l, i) => (
                          <div key={i} className="text-xs leading-tight">
                            <span className="font-mono-plex text-slate-500">{l.account_code}</span> {l.account_name}
                            <span className="ml-1 tabular-nums text-rose-700">{formatIDR(l.credit)}</span>
                          </div>
                        ))}
                        {!(j.lines || []).some(l => Number(l.credit) > 0) && <span className="text-xs text-slate-400">-</span>}
                      </div>
                    </TableCell>
                    <TableCell className="tabular-nums">{formatIDR(j.total)}</TableCell>
                    <TableCell>
                      {j.source && !["ai_auto_entry","ai_receipt_scan"].includes(j.source) ? (
                        <span title="Jurnal otomatis dari modul lain — revisi/hapus lewat dokumen sumbernya" data-testid={`journal-locked-${j.id}`}><Lock size={14} className="text-slate-300" /></span>
                      ) : (
                        <div className="flex gap-1">
                          <button onClick={() => openEditJournal(j)} className="text-slate-400 hover:text-amber-600" title="Edit jurnal" data-testid={`journal-edit-${j.id}`}><PencilSimple size={14} /></button>
                          <button onClick={() => setJournalHist(j)} className="text-slate-400 hover:text-sky-600" title={`Riwayat revisi${(j.audit_log||[]).length ? ` (${j.audit_log.length})` : ""}`} data-testid={`journal-hist-${j.id}`}>
                            <ClockCounterClockwise size={14} />
                            {(j.audit_log||[]).length > 0 && <span className="ml-0.5 text-[9px] font-semibold text-sky-700">{j.audit_log.length}</span>}
                          </button>
                          <button onClick={() => setJournalDel(j)} className="text-slate-400 hover:text-rose-600" title="Hapus jurnal" data-testid={`journal-del-${j.id}`}><Trash size={14} /></button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {pgJournal.pager}
          </div>
        </TabsContent>

        <TabsContent value="accounts" className="mt-6">
          <div className="flex justify-end mb-3 gap-2 flex-wrap">
            <input ref={accFileRef} type="file" accept=".xlsx,.xls" className="hidden" data-testid="coa-file-input" onChange={importCoa} />
            <Button variant="outline" size="sm" onClick={downloadCoaTemplate} data-testid="coa-template-btn"><Download size={14} className="mr-1" />Template Excel</Button>
            <Button variant="outline" size="sm" onClick={() => accFileRef.current?.click()} data-testid="coa-import-btn"><Upload size={14} className="mr-1" />Impor Excel</Button>
            <Button variant="outline" size="sm" onClick={async () => { try { await exportExcel("accounts", `bagan_akun_${new Date().toISOString().slice(0,10)}.xlsx`); toast.success("Bagan akun diekspor"); } catch (e) { toast.error(e.message); } }} data-testid="coa-export-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><Download size={14} className="mr-1" />Export Excel</Button>
            <ImportProgressDialog open={!!importProg} loading={importProg?.loading || false} fileName={importProg?.fileName} result={importProg?.result} onClose={() => setImportProg(null)} />
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={openAccNew} data-testid="new-account-btn"><Plus size={16} className="mr-1" />Akun Baru</Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-md" data-testid="accounts-table">
            <Table>
              <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>Tipe</TableHead><TableHead>Sub-tipe</TableHead><TableHead className="w-24"></TableHead></TableRow></TableHeader>
              <TableBody>
                {orderedAccounts.map((a) => (
                  <TableRow key={a.id} data-testid={`coa-row-${a.code}`} className={a.is_parent ? "bg-amber-50/50 font-semibold" : ""}>
                    <TableCell className="font-mono-plex text-xs" style={a._depth ? { paddingLeft: `${a._depth * 24 + 12}px` } : undefined}>
                      {(a._depth || 0) > 0 && <span className="text-slate-400 mr-1">↳</span>}
                      {a.code}
                      {a.is_parent && <span className="ml-1 text-[10px] px-1 rounded bg-amber-100 text-amber-700" title="Akun induk (header) — tidak boleh dipakai posting jurnal">Induk</span>}
                      {a.is_system && <span className="ml-1 text-[10px] px-1 rounded bg-slate-100 text-slate-500" title="Akun bawaan sistem — tidak bisa dihapus, hanya bisa diganti nama">Sistem</span>}
                    </TableCell>
                    <TableCell className={`font-medium ${(a._depth || 0) > 0 ? "text-slate-600" : ""}`}>{a.name}</TableCell>
                    <TableCell className="text-slate-500 capitalize">{a.type}</TableCell>
                    <TableCell className="text-slate-500 text-xs">{a.subtype || "—"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <button onClick={() => openAccEdit(a)} className="text-slate-400 hover:text-amber-600" title={a.is_system ? "Edit (hanya nama — akun sistem)" : "Edit akun"} data-testid={`coa-edit-${a.code}`}><PencilSimple size={14} /></button>
                        <button onClick={() => openRenameCode(a)} className="text-slate-400 hover:text-sky-600" title="Ubah nomor akun (termasuk akun sistem — kode lama disimpan sebagai alias)" data-testid={`coa-rename-${a.code}`}><Hash size={14} /></button>
                        {a.is_system ? (
                          <span className="text-slate-300 cursor-not-allowed" title="Akun sistem tidak bisa dihapus" data-testid={`coa-del-locked-${a.code}`}><Lock size={14} /></span>
                        ) : (
                          <button onClick={() => delAcc(a)} className="text-slate-400 hover:text-rose-600" title="Hapus akun (diblok jika sudah terpakai transaksi atau punya sub-akun)" data-testid={`coa-del-${a.code}`}><Trash size={14} /></button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Akun berlabel <b>Sistem</b> dipakai untuk jurnal otomatis — tidak dapat dihapus, hanya dapat diganti nama. Akun <b>Induk</b> hanya untuk pengelompokan/heading — tidak boleh dipakai untuk posting jurnal. Akun non-sistem yang sudah terpakai transaksi terkunci sampai transaksinya dihapus.</p>

          <Dialog open={accDlg} onOpenChange={(o) => { setAccDlg(o); if (!o) setAccEdit(null); }}>
            <DialogContent className="max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
              <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>{accEdit ? (accEdit.is_system ? "Edit Akun Sistem" : "Edit Akun") : "Akun Baru"}</DialogTitle></DialogHeader>
              <div className="space-y-3 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
                {accEdit?.is_system && (
                  <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2" data-testid="coa-system-banner">
                    Akun sistem — <b>kode</b> & <b>tipe</b> terkunci karena dipakai proses jurnal otomatis. Nama, sub-tipe, dan induk masih bisa diubah.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Kode Akun</Label><Input value={accForm.code} disabled={!!accEdit?.is_system} onChange={(e) => setAccForm({ ...accForm, code: e.target.value })} placeholder="cth: 1450" data-testid="coa-code-input" /></div>
                  <div><Label>Tipe</Label>
                    <Select value={accForm.type} onValueChange={(v) => setAccForm({ ...accForm, type: v, subtype: "", parent_id: "" })} disabled={!!accEdit?.is_system}>
                      <SelectTrigger data-testid="coa-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{ACC_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Nama Akun</Label><Input value={accForm.name} onChange={(e) => setAccForm({ ...accForm, name: e.target.value })} placeholder="cth: Persediaan Bahan Baku" data-testid="coa-name-input" /></div>
                <div><Label>Sub-tipe</Label>
                  <Select value={accForm.subtype || "__none__"} onValueChange={(v) => setAccForm({ ...accForm, subtype: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="coa-subtype-select"><SelectValue placeholder="Pilih sub-tipe" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— (tanpa sub-tipe) —</SelectItem>
                      {(subtypes[accForm.type] || []).map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded p-2">
                  <input type="checkbox" id="acc-is-parent" checked={accForm.is_parent} onChange={(e) => setAccForm({ ...accForm, is_parent: e.target.checked, parent_id: e.target.checked ? "" : accForm.parent_id })} data-testid="coa-is-parent-check" />
                  <Label htmlFor="acc-is-parent" className="text-xs font-medium cursor-pointer">Ini akun induk (header) — hanya untuk pengelompokan, tidak dipakai posting jurnal</Label>
                </div>
                {!accForm.is_parent && (
                  <div><Label>Akun Induk <span className="text-slate-400 text-[11px] font-normal">(opsional — pilih bila ini sub-akun)</span></Label>
                    <Select value={accForm.parent_id || "__none__"} onValueChange={(v) => setAccForm({ ...accForm, parent_id: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid="coa-parent-select"><SelectValue placeholder="Tanpa induk (akun utama)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— (tanpa induk) —</SelectItem>
                        {parentCandidates.length === 0 ? (
                          <SelectItem value="__no-parent__" disabled>Belum ada akun induk untuk tipe {accForm.type}. Buat akun dengan centang "akun induk" dulu.</SelectItem>
                        ) : parentCandidates.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 border-t border-slate-200 bg-background px-6 py-3">
                  <Button onClick={saveAcc} disabled={!accForm.code || !accForm.name} className="w-full bg-slate-900" data-testid="coa-save-btn">Simpan</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={!!renameAcc} onOpenChange={(o) => { if (!o) setRenameAcc(null); }}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Ubah Nomor Akun</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="text-xs bg-sky-50 border border-sky-200 text-sky-800 rounded p-2">
                  Kode akun <b>{renameAcc?.code}</b> akan diganti. Kode lama disimpan sebagai alias — jurnal otomatis yang masih mereferensikan kode lama akan tetap mendarat di akun ini. Semua referensi eksisting (jurnal, mapping Kas & Bank, aktiva tetap, dokumen penjualan/pembelian) ikut diperbarui otomatis.
                </div>
                <div>
                  <Label>Nomor baru</Label>
                  <Input value={renameCode} onChange={(e) => setRenameCode(e.target.value)} placeholder="cth: 1050" data-testid="coa-rename-input" autoFocus />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setRenameAcc(null)}>Batal</Button>
                  <Button onClick={doRenameCode} disabled={!renameCode || renameCode === renameAcc?.code} className="bg-sky-700 hover:bg-sky-800 text-white" data-testid="coa-rename-save">Simpan Perubahan</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>

      {/* Dialog cetak laporan keuangan — di luar Tabs agar tetap ter-mount dari tab manapun */}
      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent className="max-w-md" data-testid="print-financial-dialog">
          <DialogHeader><DialogTitle>Cetak Laporan Keuangan</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600 text-xs">Pilih periode untuk Laba Rugi & Arus Kas. Neraca ditampilkan per tanggal akhir periode.</p>
            <label className="flex items-center gap-2 cursor-pointer rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <input type="checkbox" checked={printAllTime} onChange={(e) => setPrintAllTime(e.target.checked)} data-testid="print-all-time-checkbox" className="accent-slate-900" />
              <span className="text-xs"><b>Semua Periode</b> — samakan dengan preview Ringkasan (seluruh jurnal tanpa filter tanggal)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Dari Tanggal</Label>
                <Input type="date" value={printRange.date_from} onChange={(e) => setPrintRange({ ...printRange, date_from: e.target.value })} data-testid="print-date-from" disabled={printAllTime} />
              </div>
              <div>
                <Label>Sampai Tanggal</Label>
                <Input type="date" value={printRange.date_to} onChange={(e) => setPrintRange({ ...printRange, date_to: e.target.value })} data-testid="print-date-to" disabled={printAllTime} />
              </div>
            </div>
            <div className="text-xs text-slate-500 space-y-1 rounded-md bg-slate-50 border border-slate-200 p-3">
              <div><b>Isi PDF:</b></div>
              <div>1. Neraca (per {printAllTime ? "hari ini" : formatDate(printRange.date_to)})</div>
              <div>2. Laporan Laba Rugi ({printAllTime ? "Semua Periode" : `${formatDate(printRange.date_from)} — ${formatDate(printRange.date_to)}`})</div>
              <div>3. Laporan Arus Kas (kas awal, penerimaan, pengeluaran, kas akhir)</div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setPrintOpen(false)}>Batal</Button>
            <Button className="bg-slate-900" onClick={printFinancialStatement} data-testid="print-financial-confirm-btn">Cetak PDF</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
